import { Form } from '@hologram-dimension/form';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { TextareaField } from '@hologram-dimension/textarea-field';
import useDevice from 'api/legacy/devices/useDevice';
import useSimulateMessageToApi from 'api/legacy/devices/useSimulateMessageToApi';
import Field from 'components/Field/Field';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';
import styles from './SimulateMessageToApi.module.css';

interface SimulateMessageToApiProps extends Partial<SimulateMessageToApiFormProps> {
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims?: Array<HologramSimId>) => void;
  onError: (error?: string) => void;
}

export interface SimulateMessageToApiFormProps {
  topics: string;
  data: string;
}

const SimulateMessageToApi = forwardRef<OnSubmit, SimulateMessageToApiProps>(
  ({ sims, onProcessing, onDone, onError, topics: defaultTopics, data: defaultData }, ref) => {
    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<SimulateMessageToApiFormProps>({
      mode: 'onSubmit',
      defaultValues: { topics: defaultTopics, data: defaultData },
    });

    const simId = sims.keys().next().value;
    const deviceId = sims.values().next().value;

    const organizationId = useOrganization();
    const { device } = useDevice(deviceId, organizationId);
    const ignoredTopics = useMemo(() => new Set(['_RESTAPI_', `_DEVICE_${deviceId}`]), [deviceId]);
    const doSimulateMessageToApi = useSimulateMessageToApi(deviceId);

    const commitStateChange = useCallback<SubmitHandler<SimulateMessageToApiFormProps>>(
      async (data) => {
        onProcessing(true);

        const topics = data.topics
          .split(',')
          .map((topic) => topic.trim())
          .filter((topic) => topic.length > 0 && !ignoredTopics.has(topic));

        try {
          const result = await doSimulateMessageToApi(data.data, topics);

          if (result?.success) {
            onDone([simId!]);
          } else {
            onError(result?.error);
          }
        } catch (ex) {
          if (typeof ex === 'string') {
            onError(ex);
          } else {
            onError(`An error occurred: ${ex}`);
            // eslint-disable-next-line no-console
            console.error(ex);
          }
        } finally {
          onProcessing(false);
        }
      },
      [doSimulateMessageToApi, ignoredTopics, onDone, onError, onProcessing, simId]
    );

    const onSubmit = handleSubmit(commitStateChange);

    const formRef = useRef<HTMLFormElement>(null);

    const manualSubmit = useCallback(async () => {
      formRef.current?.requestSubmit();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: manualSubmit,
      }),
      [manualSubmit]
    );

    return (
      <Form onSubmit={onSubmit} ref={formRef}>
        <div className={styles.deviceInfo}>
          {device?.name && <Field label="Device name" value={device.name} direction="column" />}
          <Field label="Device ID" value={deviceId} direction="column" />
        </div>
        <TextInputField
          label="Topics"
          helpText="A comma-separated list of topics to send with the simulated message. System topics, such as _DEVICE_X_, will be removed."
          optional
          layoutDirection="column"
          invalid={!!errors.topics}
          validationMessage={errors.topics?.message}
          {...register('topics')}
        />
        <TextareaField
          label="Data"
          helpText="The data payload of the simulated message. Can be a string or valid JSON (double-quotes only)."
          required
          layoutDirection="column"
          invalid={!!errors.data}
          validationMessage={errors.data?.message}
          inputProps={{ rows: 5 }}
          {...register('data', { required: 'Data is required' })}
        />
      </Form>
    );
  }
);

export default SimulateMessageToApi;
